import React from "react";
import { Close as CloseIcon } from "@mui/icons-material";
import EmptyShipmentImage from "@/assets/empty-state-shipment.png";
import StatusBadge from "@/components/StatusBadge";

const ShipmentDetailsModal = ({
  isOpen,
  onClose,
  shipmentDetails,
  loadingShipment,
  chatTitle
}) => {
  if (!isOpen) return null;

  const formatAddress = (location) => {
    if (!location) return 'N/A';
    return `${location.address || ''}, ${location.city || ''}, ${location.state || ''} ${location.zip || ''}`.trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="fixed right-0 top-0 h-[96%] mx-4 mt-4 rounded-[16px] my-auto w-[500px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="0.5" y="0.5" width="23" height="23" rx="5.5" fill="white" />
                  <rect x="0.5" y="0.5" width="23" height="23" rx="5.5" stroke="#E4E7EC" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.5 16.5C10.1531 16.5 10.7087 16.0826 10.9146 15.5H14.0854C14.2913 16.0826 14.8469 16.5 15.5 16.5C16.1531 16.5 16.7087 16.0826 16.9146 15.5H17C17.2761 15.5 17.5 15.2761 17.5 15V13.3183C17.5 13.0385 17.4413 12.7618 17.3276 12.506L16.8528 11.4377C16.5318 10.7155 15.8156 10.25 15.0252 10.25H14V9.5C14 8.39543 13.1046 7.5 12 7.5H8.5C7.39543 7.5 6.5 8.39543 6.5 9.5V14.5C6.5 15.0523 6.94772 15.5 7.5 15.5H8.08535C8.29127 16.0826 8.84689 16.5 9.5 16.5ZM7.5 9.5C7.5 8.94772 7.94772 8.5 8.5 8.5H12C12.5523 8.5 13 8.94772 13 9.5V14.5H10.9146C10.7087 13.9174 10.1531 13.5 9.5 13.5C8.84689 13.5 8.29127 13.9174 8.08535 14.5H7.5V9.5ZM16.4138 12.9122C16.4706 13.04 16.5 13.1784 16.5 13.3183V13.8819C16.2346 13.6444 15.8842 13.5 15.5 13.5C14.8469 13.5 14.2913 13.9174 14.0854 14.5H14V11.25H15.0252C15.4204 11.25 15.7785 11.4827 15.939 11.8439L16.4138 12.9122ZM9 15C9 14.7239 9.22386 14.5 9.5 14.5C9.77614 14.5 10 14.7239 10 15C10 15.2761 9.77614 15.5 9.5 15.5C9.22386 15.5 9 15.2761 9 15ZM15 15C15 14.7239 15.2239 14.5 15.5 14.5C15.7761 14.5 16 14.7239 16 15C16 15.2761 15.7761 15.5 15.5 15.5C15.2239 15.5 15 15.2761 15 15Z"
                    fill="#865503"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-[18px] font-[600] text-[#000]">{chatTitle}</h2>
                <p className="text-[14px] text-[#475367]">
                  <span className="border-r-[1px] font-[500] border-[#475367] pr-2">
                    #{shipmentDetails?.booking_id || 'SP-2024-101'}
                  </span>{" "}
                  {shipmentDetails ? 
                    `${shipmentDetails.pickup?.city || shipmentDetails.pickup?.address?.split(',')[0] || 'Unknown'} → ${shipmentDetails.dropoff?.city || shipmentDetails.dropoff?.address?.split(',')[0] || 'Unknown'}` 
                    : 'Los Angeles → New York'
                  }
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <CloseIcon sx={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Tabs */}
            <div className="flex justify-between border-b border-gray-200 text-[#344054] font-[500] text-[16px]">
              <button className="px-6 py-3 w-1/3 text-[#1671D9] border-b-2 border-[#1671D9]">Details</button>
              <button className="px-6 py-3 w-1/3">Payments</button>
              <button className="px-6 py-3 w-1/3">Documents</button>
            </div>

            <div className="p-6">
              {/* Location Section */}
              <div className="mb-8">
                <h3 className="text-[16px] font-[600] text-[#1D2739] mb-4">Location</h3>
                <div className="border border-[#E4E7EC] rounded-lg p-6 py-12 text-center bg-gray-50">
                  <div className="w-full h-48 bg-gray-200 rounded-lg mb-4 flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <div className="text-right mb-2">
                    <button className="text-[#1671D9] text-[14px] font-[500] flex items-center gap-1 ml-auto">
                      View Maps
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              <div className="mb-8">
                <h3 className="text-[16px] font-[600] text-[#1D2739] mb-4">Delivery Details</h3>
                <div className="space-y-3">
                  {loadingShipment ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1671D9] mx-auto"></div>
                    </div>
                  ) : shipmentDetails ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Booking ID</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {shipmentDetails.booking_id || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Pick-Up Location:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {formatAddress(shipmentDetails.pickup)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Drop-Off Location:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {formatAddress(shipmentDetails.dropoff)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Booking Date:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {formatDate(shipmentDetails.booking_date)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Dispatch Date:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          -
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">ETA:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          -
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-[16px] text-[#1D2739]">Status:</span>
                        <StatusBadge status={shipmentDetails.status} />
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-[#475367]">No shipment details available</div>
                  )}
                </div>
              </div>

              {/* Vehicle Details */}
              <div>
                <h3 className="text-[16px] font-[600] text-[#101928] mb-4">Vehicle Details</h3>
                <div className="space-y-3">
                  {loadingShipment ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1671D9] mx-auto"></div>
                    </div>
                  ) : shipmentDetails?.vehicle ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Year/Make/Model:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {`${shipmentDetails.vehicle?.year || ''} ${shipmentDetails.vehicle?.make || ''} ${shipmentDetails.vehicle?.model || ''}`.trim() || chatTitle || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">VIN:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {shipmentDetails.vehicle?.vin || 'IHGCM82633AI23456'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[16px] text-[#1D2739]">Colour:</span>
                        <span className="w-1/2 text-wrap text-[16px] text-right font-[500] text-[#1D2739]">
                          {shipmentDetails.vehicle?.color || 'Silver'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4 text-[#475367]">No vehicle details available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetailsModal;